import React, { useMemo } from "react"
import { Dialog, Box, Button, Stack, Typography } from "@mui/material"
import BcIcon from "../../img/nut2x.png"
import "./tokens.css"
import "./ClaimCelebration.css"

// Full-screen celebration modal — ACHIEVEMENT UNLOCKED.
// Adapted from handoff/src/concept-b.jsx with proper entrance animations:
// - Confetti: ~80 pieces, each with randomized duration/delay/drift falling through the viewport.
// - Star badge: stroke draws in, then fill fades, then number pops. Background glow pulses.
// - Supporting copy fades up in sequence.
const CONFETTI_COLORS = ["#FCD5CA", "#03907F", "#92278F", "#FFE9A8", "#AAD4CE", "#F05125"]

function buildConfetti(count, seed) {
    // Deterministic per-mount (same `open` re-trigger = new array) via useMemo below.
    const pieces = []
    for (let i = 0; i < count; i++) {
        const rand = (n) => ((Math.sin(seed + i * 17.3 + n * 31.7) + 1) / 2)
        const left = rand(1) * 100
        const duration = 3.2 + rand(2) * 3.6           // 3.2s..6.8s — graceful, not rushed
        const delay = -rand(3) * duration              // negative delay → staggered mid-animation
        const sizeW = 6 + rand(4) * 8
        const sizeH = 12 + rand(5) * 10
        const skew = rand(6) * 40 - 20
        const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length]
        pieces.push({ left, duration, delay, sizeW, sizeH, skew, color })
    }
    return pieces
}

export default function ClaimCelebration({ open, onClose, claimedLevel, rewardBelcoins }) {
    // Fresh seed every open so the confetti pattern varies between claims.
    const seed = useMemo(() => (open ? Date.now() : 0), [open])
    const confetti = useMemo(() => buildConfetti(80, seed), [seed])

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen
            PaperProps={{
                sx: {
                    background: "radial-gradient(ellipse at top, #F05125 0%, #2F2928 70%)",
                    color: "#fff",
                    overflow: "hidden",
                    position: "relative",
                },
            }}
        >
            {/* Confetti layer */}
            <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
                {confetti.map((p, i) => (
                    <span
                        key={i}
                        className="xp-confetti"
                        style={{
                            left: `${p.left}%`,
                            width: p.sizeW,
                            height: p.sizeH,
                            background: p.color,
                            animationDuration: `${p.duration}s`,
                            animationDelay: `${p.delay}s`,
                            transform: `skew(${p.skew}deg)`,
                        }}
                    />
                ))}
            </Box>

            <Stack
                alignItems="center"
                justifyContent="center"
                spacing={3}
                sx={{ height: "100%", px: 3, py: 6, position: "relative", zIndex: 1 }}
            >
                <Typography
                    className="xp-fade-in"
                    sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        letterSpacing: ".22em",
                        opacity: 0.9,
                        textTransform: "uppercase",
                        animationDelay: "200ms",
                    }}
                >
                    Achievement unlocked
                </Typography>

                <Typography
                    className="xp-fade-in"
                    sx={{
                        fontSize: { xs: 44, sm: 64, md: 72 },
                        fontWeight: 700,
                        textTransform: "uppercase",
                        textAlign: "center",
                        lineHeight: 1,
                        animationDelay: "350ms",
                    }}
                >
                    Level up!
                </Typography>

                {/* Star badge — stroke draws in, then fill fades, number pops last. */}
                <Box className="xp-star-wrap" sx={{ position: "relative", width: 240, height: 240 }}>
                    <svg viewBox="0 0 100 100" width="100%" height="100%">
                        <defs>
                            <radialGradient id="xp-star-grad" cx="50%" cy="45%" r="60%">
                                <stop offset="0%" stopColor="#FFC78A" />
                                <stop offset="55%" stopColor="#F05125" />
                                <stop offset="100%" stopColor="#B8300D" />
                            </radialGradient>
                        </defs>
                        <path
                            className="xp-star-fill"
                            d="M50 3 L61 37 L97 37 L68 59 L79 93 L50 72 L21 93 L32 59 L3 37 L39 37 Z"
                            fill="url(#xp-star-grad)"
                        />
                        <path
                            className="xp-star-outline"
                            d="M50 3 L61 37 L97 37 L68 59 L79 93 L50 72 L21 93 L32 59 L3 37 L39 37 Z"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="2.5"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <Typography
                        className="xp-star-number"
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "grid",
                            placeItems: "center",
                            fontSize: 96,
                            fontWeight: 700,
                            color: "#fff",
                            textShadow: "0 2px 10px rgba(0,0,0,0.35)",
                        }}
                    >
                        {claimedLevel}
                    </Typography>
                </Box>

                <Typography
                    className="xp-fade-in"
                    sx={{ fontSize: 20, opacity: 0.9, textAlign: "center", animationDelay: "1300ms" }}
                >
                    Level <b>{claimedLevel}</b> reached
                </Typography>

                {/* Reward pill */}
                <Box
                    className="xp-fade-in"
                    sx={{
                        mt: 2,
                        px: { xs: 3, sm: 5 },
                        py: 2,
                        background: "rgba(255,255,255,0.1)",
                        borderRadius: 999,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        animationDelay: "1450ms",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: ".16em",
                            textTransform: "uppercase",
                            opacity: 0.75,
                        }}
                    >
                        Reward
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <img src={BcIcon} alt="belcoin" width={40} height={40} />
                        <Typography sx={{ fontSize: 32, fontWeight: 700 }}>+{rewardBelcoins}</Typography>
                    </Box>
                </Box>

                <Stack
                    className="xp-fade-in"
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    sx={{ mt: 4, animationDelay: "1600ms" }}
                >
                    <Button
                        onClick={onClose}
                        variant="contained"
                        sx={{
                            bgcolor: "#fff",
                            color: "#2F2928",
                            textTransform: "uppercase",
                            px: 4,
                            py: 1.5,
                            borderRadius: 999,
                            fontWeight: 700,
                            "&:hover": { bgcolor: "#f0e6e1" },
                        }}
                    >
                        Back to my XP
                    </Button>
                </Stack>
            </Stack>
        </Dialog>
    )
}
